import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { BookOpenCheck, Search, Send, Bot, User, Loader2, Sparkles, CheckCircle2, AlertCircle, Ban, Pencil, ArrowRight, Wand2, FileDown, AlertTriangle, History } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { exportListPdf } from '../lib/exportPdf';
import { useAuth } from '../context/AuthContext';
import { MarkdownMessage } from '../components/MarkdownMessage';

const ESCLUSO = 'Escluso';
const FILTRI = [
  { v: 'tutti', l: 'Tutti' },
  { v: 'da_classificare', l: 'Da classificare' },
  { v: 'classificati', l: 'Classificati' },
  { v: 'esclusi', l: 'Esclusi' },
];
const SUGGESTIONS = [
  'Quali conti non sono ancora classificati?',
  'Elenca i conti della sezione Costi Commerciali',
  'Ci sono conti con classificazione incoerente?',
  'Riassumi la struttura del piano dei conti',
];

/* ---------- DIALOG SUGGERIMENTI AI ---------- */
const SuggestDialog = ({ open, onOpenChange, loading, suggerimenti, sezioni, onApply }) => {
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && suggerimenti) {
      const init = {};
      suggerimenti.forEach((s) => { if (s.sezione_suggerita) init[s.conto] = s.sezione_suggerita; });
      setSel(init);
    }
  }, [open, suggerimenti]);
  const list = suggerimenti || [];
  const done = list.filter((s) => sel[s.conto]).length;
  const apply = async () => {
    const map = {};
    list.forEach((s) => { if (sel[s.conto]) map[s.conto] = sel[s.conto]; });
    if (!Object.keys(map).length) return;
    setBusy(true);
    try { await onApply(map); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="piano-suggest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Wand2 className="h-4 w-4 text-primary" />Suggerimenti AI · classificazione conti</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />L'AI sta analizzando i conti…</div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground -mt-1">L'AI ha proposto una sezione per ogni conto. Controlla e modifica dove serve, poi applica: le scelte aggiornano il Piano dei Conti (unica fonte).</p>
            <div className="max-h-[48vh] overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow><TableHead className="w-[110px]">Conto</TableHead><TableHead>Descrizione</TableHead><TableHead className="w-[220px]">Sezione (AI)</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((s, i) => (
                    <TableRow key={s.conto} data-testid={`piano-suggest-row-${i}`} className={sel[s.conto] ? '' : 'bg-[hsl(var(--warning))]/5'}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground">{s.conto}</TableCell>
                      <TableCell className="text-sm">{s.descrizione}</TableCell>
                      <TableCell>
                        <Select value={sel[s.conto] || undefined} onValueChange={(v) => setSel((p) => ({ ...p, [s.conto]: v }))}>
                          <SelectTrigger className="h-8" data-testid={`piano-suggest-sel-${s.conto}`}><SelectValue placeholder="Non proposto" /></SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>Sezioni CE</SelectLabel>
                              {(sezioni || []).map((sz) => <SelectItem key={sz} value={sz}>{sz}</SelectItem>)}
                            </SelectGroup>
                            <SelectGroup>
                              <SelectLabel>Altro</SelectLabel>
                              <SelectItem value={ESCLUSO}>Escluso (patrimoniale)</SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {list.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Nessun conto da classificare 🎉</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-sm text-muted-foreground tabular-nums">{done}/{list.length} pronti</span>
              <Button onClick={apply} disabled={busy || done === 0} data-testid="piano-suggest-apply">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Applica {done} classificazioni
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------- DIALOG MODIFICA CLASSIFICAZIONE (procedura con conferma) ---------- */
const sezLabel = (v) => (v === '' || v == null ? 'Da classificare' : (v === ESCLUSO ? 'Escluso (patrimoniale)' : v));
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const ChangeClassDialog = ({ row, sezioni, onConfirm, onClose }) => {
  const [sez, setSez] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => { setSez(row?.sezione || ''); setConfirmOpen(false); }, [row]);
  if (!row) return null;
  const effective = sez === '__CLEAR__' ? '' : sez;
  const changed = effective !== (row.sezione || '');
  // Sezione del bilancio riclassificato UFFICIALE caricato (schema annuale). Se il conto è
  // presente nel bilancio ufficiale e la nuova sezione differisce → disallineamento.
  const ufficiale = row.sezione_ufficiale || null;
  const misaligned = changed && !!ufficiale && effective !== ufficiale;

  const doConfirm = async () => {
    setBusy(true);
    try { await onConfirm(row.conto, sez); } finally { setBusy(false); setConfirmOpen(false); }
  };
  const onConfirmClick = () => { if (misaligned) setConfirmOpen(true); else doConfirm(); };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="piano-edit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Pencil className="h-4 w-4 text-primary" />Modifica classificazione</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border p-3">
            <div className="text-[11px] text-muted-foreground">Conto</div>
            <div className="text-sm font-medium tabular-nums">{row.conto}</div>
            <div className="text-sm text-muted-foreground">{row.descrizione}</div>
            {ufficiale && (
              <div className="mt-2 pt-2 border-t flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 text-[hsl(var(--success))]" />
                Bilancio ufficiale: <Badge variant="outline" className="text-[10px]">{ufficiale}</Badge>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="text-muted-foreground">Attuale:</span>
            <Badge variant="outline" className="text-[10px]">{row.sezione || 'Da classificare'}</Badge>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Nuova sezione:</span>
          </div>
          <Select value={sez || undefined} onValueChange={setSez}>
            <SelectTrigger data-testid="piano-edit-select"><SelectValue placeholder="Seleziona sezione…" /></SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Sezioni CE</SelectLabel>
                {(sezioni || []).map((sz) => <SelectItem key={sz} value={sz}>{sz}</SelectItem>)}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>Altro</SelectLabel>
                <SelectItem value={ESCLUSO}>Escluso (patrimoniale)</SelectItem>
                <SelectItem value="__CLEAR__">Da classificare (rimuovi)</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {misaligned ? (
            <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 flex gap-2" data-testid="piano-edit-misalign-warn">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
              <p className="text-[12px] text-foreground/80 leading-relaxed">
                Questa classificazione <b>differisce dal bilancio riclassificato ufficiale</b> (ufficiale: <b>{sezLabel(ufficiale)}</b>). Confermando, il Piano dei Conti risulterà <b>disallineato</b> dal bilancio ufficiale caricato.
              </p>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Il Piano dei Conti è l'unica fonte: questa modifica verrà applicata a <b className="text-foreground">tutti i bilanci di periodo</b>.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="piano-edit-cancel">Annulla</Button>
          <Button onClick={onConfirmClick} disabled={!changed || !sez || busy} data-testid="piano-edit-confirm"
            className={misaligned ? 'bg-[hsl(var(--warning))] text-white hover:bg-[hsl(var(--warning))]/90' : ''}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (misaligned ? <AlertTriangle className="h-4 w-4 mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />)}
            Conferma modifica
          </Button>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="piano-misalign-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))]" />Disallineamento dal bilancio ufficiale
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <p>Stai per classificare il conto <b className="text-foreground tabular-nums">{row.conto}</b> ({row.descrizione}) in modo diverso dal bilancio riclassificato ufficiale caricato.</p>
                  <div className="rounded-lg border p-3 flex items-center justify-center gap-3 text-[13px]">
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Ufficiale</div>
                      <div className="font-medium">{sezLabel(ufficiale)}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-[hsl(var(--warning))]" />
                    <div className="text-center">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Nuova</div>
                      <div className="font-medium text-[hsl(var(--warning))]">{sezLabel(effective)}</div>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-[12px]">La modifica avrà effetto su <b className="text-foreground">tutti i bilanci di periodo</b>, che non corrisponderanno più al bilancio ufficiale per questo conto. Vuoi procedere?</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="piano-misalign-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction onClick={doConfirm} disabled={busy}
                className="bg-[hsl(var(--warning))] text-white hover:bg-[hsl(var(--warning))]/90" data-testid="piano-misalign-confirm">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}Procedi comunque
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default function PianoConti() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filtro, setFiltro] = useState('tutti');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [editRow, setEditRow] = useState(null);  // {conto, descrizione, sezione}
  const [suggest, setSuggest] = useState({ open: false, loading: false, items: [], sezioni: [] });
  const [log, setLog] = useState([]);
  const chatEndRef = useRef(null);

  const load = () => { setLoading(true); return api.getFierPianoConti().then(setData).catch(() => toast.error('Errore nel caricamento del piano dei conti')).finally(() => setLoading(false)); };
  const loadLog = () => api.getFierPianoContiLog().then((r) => setLog(r.log || [])).catch(() => {});
  useEffect(() => { load(); loadLog(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const applyChange = async (conto, sezione) => {
    const val = sezione === '__CLEAR__' ? '' : sezione;
    try { const d = await api.classificaFierPianoConti(undefined, { [conto]: val }); setData(d); setEditRow(null); loadLog(); toast.success('Classificazione aggiornata · valida in tutta l\u2019app'); }
    catch { toast.error('Errore nell\u2019aggiornamento'); }
  };

  const exportPdf = () => {
    const rows = data?.conti || [];
    if (!rows.length) { toast.error('Nessun conto da esportare'); return; }
    exportListPdf({
      company: 'Fier S.p.A.',
      title: 'Piano dei Conti',
      period: `Elenco consolidato · ${rows.length} conti`,
      columns: [
        { label: 'Conto', align: 'left', render: (c) => c.conto },
        { label: 'Descrizione', align: 'left', render: (c) => c.descrizione },
        { label: 'Presente in', align: 'left', render: (c) => (c.fonti || []).join(', ') },
        {
          label: 'Classificazione', align: 'left',
          render: (c) => {
            if (c.sezione === ESCLUSO) return { html: '<span class="tag tag-muted">Escluso (patrimoniale)</span>' };
            if (c.sezione) return { html: `<span class="tag">${String(c.sezione).replace(/</g, '&lt;')}</span>` };
            return { html: '<span class="tag tag-warn">Da classificare</span>' };
          },
        },
      ],
      rows,
    });
  };

  const runSuggest = async () => {
    setSuggest({ open: true, loading: true, items: [], sezioni: data?.sezioni_disponibili || [] });
    try {
      const res = await api.suggerisciFierPianoConti();
      setSuggest({ open: true, loading: false, items: res.suggerimenti || [], sezioni: res.sezioni_disponibili || data?.sezioni_disponibili || [] });
    } catch {
      toast.error('Errore nel generare i suggerimenti AI');
      setSuggest((s) => ({ ...s, open: false, loading: false }));
    }
  };
  const applySuggest = async (map) => {
    try { const d = await api.classificaFierPianoConti(undefined, map); setData(d); setSuggest((s) => ({ ...s, open: false })); loadLog(); toast.success(`${Object.keys(map).length} conti classificati`); }
    catch { toast.error('Errore nell\u2019applicare i suggerimenti'); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next); setInput(''); setSending(true);
    try { const res = await api.fierPianoContiChat({ message: msg, history: next.slice(0, -1) }); setMessages([...next, { role: 'assistant', content: res.answer }]); }
    catch { setMessages([...next, { role: 'assistant', content: 'Si \u00e8 verificato un errore nel generare la risposta. Riprova.' }]); toast.error('Errore assistente AI'); }
    finally { setSending(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = data?.conti || [];
    if (filtro === 'da_classificare') list = list.filter((c) => !c.sezione);
    else if (filtro === 'classificati') list = list.filter((c) => c.sezione && c.sezione !== ESCLUSO);
    else if (filtro === 'esclusi') list = list.filter((c) => c.sezione === ESCLUSO);
    if (q) list = list.filter((c) => (c.conto || '').toLowerCase().includes(q) || (c.descrizione || '').toLowerCase().includes(q));
    return list;
  }, [data, query, filtro]);

  const stats = data || {};

  return (
    <div className="space-y-6" data-testid="piano-conti-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><BookOpenCheck className="h-5 w-5 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Piano dei Conti</h1>
            <p className="text-sm text-muted-foreground">Fier S.p.A. · elenco unico e consolidato dai mastrini (annuale + periodi) · unica fonte della classificazione dei costi</p>
          </div>
        </div>
        {isAdmin && (stats.n_da_classificare || 0) > 0 && (
          <Button onClick={runSuggest} data-testid="piano-suggest-btn">
            <Wand2 className="h-4 w-4 mr-2" />Suggerisci con AI ({stats.n_da_classificare})
          </Button>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Conti totali', value: stats.n_conti, tone: 'text-foreground', icon: BookOpenCheck },
          { label: 'Classificati', value: stats.n_classificati, tone: 'text-[hsl(var(--success))]', icon: CheckCircle2 },
          { label: 'Da classificare', value: stats.n_da_classificare, tone: (stats.n_da_classificare || 0) > 0 ? 'text-[hsl(var(--warning))]' : 'text-foreground', icon: AlertCircle },
          { label: 'Esclusi (patrim.)', value: stats.n_esclusi, tone: 'text-muted-foreground', icon: Ban },
        ].map((k) => (
          <Card key={k.label} className="p-4" data-testid={`piano-kpi-${k.label}`}>
            <div className="flex items-center justify-between"><div className="text-xs text-muted-foreground">{k.label}</div><k.icon className="h-4 w-4 text-muted-foreground" /></div>
            <div className={`text-2xl font-semibold tabular-nums mt-1 ${k.tone}`}>{k.value ?? '—'}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Tabella conti */}
        <Card className="p-0 overflow-hidden xl:col-span-2">
          <div className="px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-3">
            <h3 className="text-sm font-semibold mr-auto">Elenco conti ({filtered.length})</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="h-9" onClick={exportPdf} disabled={loading} data-testid="piano-export-btn">
                <FileDown className="h-3.5 w-3.5 mr-1.5" />Esporta PDF
              </Button>
              <Select value={filtro} onValueChange={setFiltro}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="piano-filter"><SelectValue /></SelectTrigger>
                <SelectContent>{FILTRI.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
              <div className="relative w-full sm:w-56">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca conto o descrizione…" className="pl-8 h-9" data-testid="piano-search" />
              </div>
            </div>
          </div>
          <div className="max-h-[64vh] overflow-y-auto">
            {loading ? (
              <div className="p-10 text-center text-muted-foreground">Caricamento…</div>
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-[120px]">Conto</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead className="w-[150px]">Presente in</TableHead>
                    <TableHead className="w-[230px]">Classificazione</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c, i) => (
                    <TableRow key={c.conto} data-testid={`piano-row-${i}`}>
                      <TableCell className="tabular-nums text-xs text-muted-foreground">{c.conto}</TableCell>
                      <TableCell className="text-sm">{c.descrizione}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(c.fonti || []).map((f) => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-between gap-2">
                          {c.sezione === ESCLUSO
                            ? <Badge variant="outline" className="text-[10px] text-muted-foreground gap-1"><Ban className="h-3 w-3" />Escluso</Badge>
                            : c.sezione
                              ? <Badge variant="outline" className="text-[10px]">{c.sezione}{c.origine === 'schema' && <span className="ml-1 opacity-60">· auto</span>}</Badge>
                              : <Badge variant="outline" className="text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40">Da classificare</Badge>}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setEditRow(c)} data-testid={`piano-edit-${c.conto}`}>
                              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun conto trovato</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </Card>

        {/* Finestra AI */}
        <Card className="flex flex-col h-[64vh] xl:h-auto xl:min-h-[64vh]" data-testid="piano-ai-panel">
          <div className="px-5 py-3 border-b flex items-center gap-2"><Bot className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold">Assistente AI · Piano dei Conti</h3></div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground py-4">
                <Sparkles className="h-8 w-8 mx-auto mb-3 text-primary/60" />
                <p className="text-sm mb-4">Interroga l'AI sui conti e la loro classificazione.</p>
                <div className="flex flex-col gap-2 items-stretch max-w-sm mx-auto">
                  {SUGGESTIONS.map((q) => <button key={q} onClick={() => send(q)} className="text-xs px-3 py-2 rounded-lg border border-border hover:border-primary/50 hover:bg-accent transition-colors text-left" data-testid="piano-ai-suggestion">{q}</button>)}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`piano-ai-msg-${m.role}`}>
                {m.role === 'assistant' && <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div>}
                <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-muted text-foreground'}`}>
                  {m.role === 'assistant' ? <MarkdownMessage content={m.content} /> : m.content}
                </div>
                {m.role === 'user' && <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
              </div>
            ))}
            {sending && <div className="flex gap-3 justify-start"><div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-primary" /></div><div className="bg-muted rounded-xl px-4 py-2.5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Sto analizzando…</div></div>}
            <div ref={chatEndRef} />
          </div>
          <div className="border-t p-3 flex items-center gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Scrivi una domanda…" disabled={sending} data-testid="piano-ai-input" />
            <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="piano-ai-send"><Send className="h-4 w-4" /></Button>
          </div>
        </Card>
      </div>

      {/* Registro modifiche */}
      <Card className="p-0 overflow-hidden" data-testid="piano-log-card">
        <div className="px-5 py-3 border-b flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 sm:mr-auto">
            <History className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Registro modifiche ({log.length})</h3>
          </div>
          {log.some((e) => e.misaligned) && (
            <Badge variant="outline" className="text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 gap-1 w-fit">
              <AlertTriangle className="h-3 w-3" />{log.filter((e) => e.misaligned).length} disallineate dal bilancio ufficiale
            </Badge>
          )}
        </div>
        <div className="max-h-[46vh] overflow-y-auto overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-[140px]">Data / ora</TableHead>
                <TableHead className="w-[110px]">Conto</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Azione (Da → A)</TableHead>
                <TableHead className="w-[150px]">Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.map((e) => (
                <TableRow key={e.id} data-testid={`piano-log-row-${e.id}`} className={e.misaligned ? 'bg-[hsl(var(--warning))]/5' : ''}>
                  <TableCell className="text-xs text-muted-foreground tabular-nums align-top whitespace-nowrap">{fmtDate(e.at)}</TableCell>
                  <TableCell className="tabular-nums text-xs text-muted-foreground align-top">{e.conto}</TableCell>
                  <TableCell className="text-sm max-w-[160px] whitespace-normal break-words md:max-w-none">{e.descrizione || '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">{sezLabel(e.from_sez)}</Badge>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Badge variant="outline" className={`text-[10px] ${e.misaligned ? 'text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40' : 'text-foreground'}`}>{sezLabel(e.to_sez)}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {e.misaligned ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--warning))]" title={e.sezione_ufficiale ? `Ufficiale: ${e.sezione_ufficiale}` : ''}>
                        <AlertTriangle className="h-3.5 w-3.5" />Disallineato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--success))]">
                        <CheckCircle2 className="h-3.5 w-3.5" />Allineato
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {log.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Nessuna modifica registrata</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ChangeClassDialog
        row={editRow}
        sezioni={data?.sezioni_disponibili || []}
        onConfirm={applyChange}
        onClose={() => setEditRow(null)}
      />
      <SuggestDialog
        open={suggest.open}
        onOpenChange={(o) => setSuggest((s) => ({ ...s, open: o }))}
        loading={suggest.loading}
        suggerimenti={suggest.items}
        sezioni={suggest.sezioni}
        onApply={applySuggest}
      />
    </div>
  );
}
